import React, { useState, useCallback } from "react";
import { Container, Typography } from "@mui/material";
import PageHeading from "../utils/PageHeading";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            if (!form.name || !form.email || !form.subject || !form.message) {
                setError("Please fill in all required fields.");
                return;
            }
            setSending(true);
            setError("");
            setSuccess("");

            try {
                const response = await axios.post(`${API_BASE}/contact`, form);
                if (response.data.success) {
                    setSuccess(response.data.message);
                    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
                }
            } catch (err) {
                setError(
                    err.response?.data?.error || "Something went wrong. Please try again."
                );
            } finally {
                setSending(false);
            }
        },
        [form]
    );

    const inputStyle = {
        width: "100%",
        padding: "14px 18px",
        border: "1px solid rgba(67, 197, 185, 0.25)",
        borderRadius: "10px",
        fontSize: "16px",
        fontFamily: "'Nunito', sans-serif",
        background: "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        outline: "none",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        color: "#062a3f",
    };

    const inputFocusHandler = (e) => {
        e.target.style.borderColor = "#43C5B9";
        e.target.style.boxShadow = "0 0 0 3px rgba(67, 197, 185, 0.15)";
    };
    const inputBlurHandler = (e) => {
        e.target.style.borderColor = "rgba(67, 197, 185, 0.25)";
        e.target.style.boxShadow = "none";
    };

    const contactInfoItems = [
        {
            icon: "📧",
            label: "Email",
            value: "contact@rxpose.com",
            href: "mailto:contact@rxpose.com",
        },
        {
            icon: "📍",
            label: "Address",
            value: "Washington, DC, USA",
            href: null,
        },
    ];

    return (
        <div className="about_page body_bg">
            {/* Banner */}
            <div className="about_banner_main">
                <PageHeading title="contact" pageTitle="Contact Us" />
            </div>

            {/* Main Content */}
            <Container maxWidth="lg" style={{ padding: "80px 24px 100px" }}>
                <div style={{ textAlign: "center", marginBottom: "60px" }}>
                    <Typography
                        variant="h3"
                        className="heading_three"
                        style={{ marginBottom: "16px" }}
                    >
                        Get in Touch
                    </Typography>
                    <Typography
                        className="description"
                        style={{ color: "#82929b", maxWidth: "600px", margin: "0 auto" }}
                    >
                        Have questions about drug pricing, our platform, or want to get
                        involved? We'd love to hear from you.
                    </Typography>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 380px",
                        gap: "40px",
                        alignItems: "start",
                    }}
                >
                    {/* Contact Form Card */}
                    <div
                        style={{
                            background: "rgba(255, 255, 255, 0.65)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            borderRadius: "20px",
                            padding: "40px",
                            border: "1px solid rgba(67, 197, 185, 0.15)",
                            boxShadow:
                                "0 8px 40px rgba(6, 42, 63, 0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {/* Decorative gradient blob */}
                        <div
                            style={{
                                position: "absolute",
                                top: "-80px",
                                right: "-80px",
                                width: "250px",
                                height: "250px",
                                borderRadius: "50%",
                                background:
                                    "radial-gradient(circle, rgba(67,197,185,0.1) 0%, transparent 70%)",
                                pointerEvents: "none",
                            }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                bottom: "-60px",
                                left: "-60px",
                                width: "200px",
                                height: "200px",
                                borderRadius: "50%",
                                background:
                                    "radial-gradient(circle, rgba(138,219,211,0.08) 0%, transparent 70%)",
                                pointerEvents: "none",
                            }}
                        />

                        <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                            {/* Name & Email row */}
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px",
                                    marginBottom: "16px",
                                }}
                            >
                                <div>
                                    <label
                                        style={{
                                            display: "block",
                                            fontSize: "14px",
                                            fontWeight: "700",
                                            color: "#062a3f",
                                            marginBottom: "6px",
                                            fontFamily: "'Roboto', sans-serif",
                                        }}
                                    >
                                        Name <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Your full name"
                                        value={form.name}
                                        onChange={handleChange}
                                        onFocus={inputFocusHandler}
                                        onBlur={inputBlurHandler}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label
                                        style={{
                                            display: "block",
                                            fontSize: "14px",
                                            fontWeight: "700",
                                            color: "#062a3f",
                                            marginBottom: "6px",
                                            fontFamily: "'Roboto', sans-serif",
                                        }}
                                    >
                                        Email <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="you@example.com"
                                        value={form.email}
                                        onChange={handleChange}
                                        onFocus={inputFocusHandler}
                                        onBlur={inputBlurHandler}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            {/* Subject row */}
                            <div style={{ marginBottom: "16px" }}>
                                <label
                                    style={{
                                        display: "block",
                                        fontSize: "14px",
                                        fontWeight: "700",
                                        color: "#062a3f",
                                        marginBottom: "6px",
                                        fontFamily: "'Roboto', sans-serif",
                                    }}
                                >
                                    Subject <span style={{ color: "#ef4444" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="subject"
                                    placeholder="What is this about?"
                                    value={form.subject}
                                    onChange={handleChange}
                                    onFocus={inputFocusHandler}
                                    onBlur={inputBlurHandler}
                                    style={inputStyle}
                                />
                            </div>

                            {/* Message */}
                            <div style={{ marginBottom: "24px" }}>
                                <label
                                    style={{
                                        display: "block",
                                        fontSize: "14px",
                                        fontWeight: "700",
                                        color: "#062a3f",
                                        marginBottom: "6px",
                                        fontFamily: "'Roboto', sans-serif",
                                    }}
                                >
                                    Message <span style={{ color: "#ef4444" }}>*</span>
                                </label>
                                <textarea
                                    name="message"
                                    placeholder="Tell us how we can help..."
                                    value={form.message}
                                    onChange={handleChange}
                                    onFocus={inputFocusHandler}
                                    onBlur={inputBlurHandler}
                                    rows={5}
                                    style={{
                                        ...inputStyle,
                                        resize: "vertical",
                                        minHeight: "130px",
                                    }}
                                />
                            </div>

                            {/* Error */}
                            {error && (
                                <div
                                    style={{
                                        background: "rgba(254, 242, 242, 0.8)",
                                        border: "1px solid #fecaca",
                                        borderRadius: "10px",
                                        padding: "12px 16px",
                                        marginBottom: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <span style={{ color: "#dc2626", fontSize: "14px" }}>
                                        ⚠️ {error}
                                    </span>
                                </div>
                            )}

                            {/* Success */}
                            {success && (
                                <div
                                    style={{
                                        background: "rgba(236, 253, 245, 0.8)",
                                        border: "1px solid #a7f3d0",
                                        borderRadius: "10px",
                                        padding: "12px 16px",
                                        marginBottom: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <span style={{ color: "#065f46", fontSize: "14px" }}>
                                        ✅ {success}
                                    </span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={sending}
                                style={{
                                    width: "100%",
                                    padding: "16px",
                                    background: sending
                                        ? "#94a3b8"
                                        : "linear-gradient(135deg, #43C5B9 0%, #2db5a8 100%)",
                                    color: "#fff",
                                    fontSize: "17px",
                                    fontWeight: "700",
                                    fontFamily: "'Roboto', sans-serif",
                                    border: "none",
                                    borderRadius: "12px",
                                    cursor: sending ? "default" : "pointer",
                                    transition: "all 0.3s ease",
                                    boxShadow: sending
                                        ? "none"
                                        : "0 4px 15px rgba(67, 197, 185, 0.3)",
                                    letterSpacing: "0.5px",
                                }}
                                onMouseEnter={(e) => {
                                    if (!sending) {
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow =
                                            "0 6px 20px rgba(67, 197, 185, 0.4)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = sending
                                        ? "none"
                                        : "0 4px 15px rgba(67, 197, 185, 0.3)";
                                }}
                            >
                                {sending ? "Sending..." : "Send Message"}
                            </button>
                        </form>
                    </div>

                    {/* Sidebar */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                        {/* Contact Info Card */}
                        <div
                            style={{
                                background: "rgba(255, 255, 255, 0.65)",
                                backdropFilter: "blur(20px)",
                                WebkitBackdropFilter: "blur(20px)",
                                borderRadius: "20px",
                                padding: "32px",
                                border: "1px solid rgba(67, 197, 185, 0.15)",
                                boxShadow:
                                    "0 8px 40px rgba(6, 42, 63, 0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
                            }}
                        >
                            <h3
                                style={{
                                    fontSize: "20px",
                                    fontWeight: "800",
                                    color: "#062a3f",
                                    margin: "0 0 24px",
                                    fontFamily: "'Roboto', sans-serif",
                                }}
                            >
                                Contact Information
                            </h3>

                            {contactInfoItems.map((item, i) => (
                                <div
                                    key={i}
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "14px",
                                        marginBottom: i < contactInfoItems.length - 1 ? "20px" : 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "44px",
                                            height: "44px",
                                            borderRadius: "12px",
                                            background:
                                                "linear-gradient(135deg, rgba(67,197,185,0.15), rgba(138,219,211,0.1))",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "20px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div
                                            style={{
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#82929b",
                                                marginBottom: "4px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                            }}
                                        >
                                            {item.label}
                                        </div>
                                        {item.href ? (
                                            <a
                                                href={item.href}
                                                style={{
                                                    fontSize: "15px",
                                                    fontWeight: "700",
                                                    color: "#062a3f",
                                                    textDecoration: "none",
                                                    transition: "color 0.2s",
                                                }}
                                                onMouseEnter={(e) =>
                                                    (e.currentTarget.style.color = "#43C5B9")
                                                }
                                                onMouseLeave={(e) =>
                                                    (e.currentTarget.style.color = "#062a3f")
                                                }
                                            >
                                                {item.value}
                                            </a>
                                        ) : (
                                            <span
                                                style={{
                                                    fontSize: "15px",
                                                    fontWeight: "700",
                                                    color: "#062a3f",
                                                }}
                                            >
                                                {item.value}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Office Hours Card */}
                        <div
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(67,197,185,0.08), rgba(235,248,242,0.6))",
                                backdropFilter: "blur(12px)",
                                WebkitBackdropFilter: "blur(12px)",
                                borderRadius: "20px",
                                padding: "32px",
                                border: "1px solid rgba(67, 197, 185, 0.12)",
                            }}
                        >
                            <h3
                                style={{
                                    fontSize: "20px",
                                    fontWeight: "800",
                                    color: "#062a3f",
                                    margin: "0 0 20px",
                                    fontFamily: "'Roboto', sans-serif",
                                }}
                            >
                                Office Hours
                            </h3>
                            {[
                                { day: "Monday – Friday", time: "9:00 AM – 6:00 PM EST" },
                                { day: "Saturday", time: "10:00 AM – 2:00 PM EST" },
                                { day: "Sunday", time: "Closed" },
                            ].map((item, i) => (
                                <div
                                    key={i}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        padding: "10px 0",
                                        borderBottom:
                                            i < 2
                                                ? "1px solid rgba(67, 197, 185, 0.1)"
                                                : "none",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "14px",
                                            fontWeight: "700",
                                            color: "#062a3f",
                                        }}
                                    >
                                        {item.day}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: "14px",
                                            color: item.time === "Closed" ? "#ef4444" : "#43C5B9",
                                            fontWeight: "600",
                                        }}
                                    >
                                        {item.time}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Quick Links Card */}
                        <div
                            style={{
                                background: "rgba(6, 42, 63, 0.03)",
                                borderRadius: "20px",
                                padding: "32px",
                                border: "1px solid rgba(6, 42, 63, 0.06)",
                            }}
                        >
                            <h3
                                style={{
                                    fontSize: "20px",
                                    fontWeight: "800",
                                    color: "#062a3f",
                                    margin: "0 0 16px",
                                    fontFamily: "'Roboto', sans-serif",
                                }}
                            >
                                Quick Links
                            </h3>
                             {[
                                { label: "Browse Medications", href: "/medicines", icon: "💊" },
                                { label: "Contact Congressman", href: "/contact-congressman", icon: "🏛️" },
                                { label: "Healthcare in America", href: "/healthcare", icon: "📰" },
                            ].map((link, i) => (
                                <a
                                    key={i}
                                    href={link.href}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        color: "#062a3f",
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        textDecoration: "none",
                                        transition: "all 0.2s ease",
                                        marginBottom: i < 2 ? "6px" : "0",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "rgba(67, 197, 185, 0.08)";
                                        e.currentTarget.style.color = "#43C5B9";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "transparent";
                                        e.currentTarget.style.color = "#062a3f";
                                    }}
                                >
                                    <span>{link.icon}</span>
                                    {link.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
}
